import { motion } from "framer-motion";
import { VIDEO, CONTACT } from "@/veedu/data";
import { MotionBackgroundVideo } from "./Shared";
import { useReservation } from "@/context/ReservationContext";

const EASE = [0.16, 1, 0.3, 1];

// Flourish icon on left/right of "Come and listen to the"
const SideFlourish = ({ flip = false }) => (
  <svg
    width="38"
    height="16"
    viewBox="0 0 38 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`inline-block opacity-75 ${flip ? "transform scale-x-[-1]" : ""}`}
  >
    <path
      d="M2 12C7 12 9 4 15 4C21 4 21 12 27 12C32 12 34 8 36 8M36 8C33 8 31 5 28 5M36 8C34 10 32 13 29 13"
      stroke="#C2A04A"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <circle cx="36" cy="8" r="1.2" fill="#C2A04A" />
  </svg>
);

// Kerala Banana Leaf SVG — realistic wide blade with midrib & parallel veins
const BananaLeaf = ({ flip = false, className = "" }) => (
  <svg
    width="70"
    height="34"
    viewBox="0 0 70 34"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`inline-block ${className}`}
    style={{ transform: flip ? "scaleX(-1)" : "none" }}
  >
    {/* Leaf blade — wide elongated ellipse tapering to a tip on the right */}
    <path
      d="M4 17 C6 8 16 3 30 2 C44 1 60 6 66 13 C68 15 68 17 66 19 C60 26 44 31 30 32 C16 33 6 26 4 17 Z"
      fill="#C2A04A"
      opacity="0.15"
    />
    {/* Leaf outline */}
    <path
      d="M4 17 C6 8 16 3 30 2 C44 1 60 6 66 13 C68 15 68 17 66 19 C60 26 44 31 30 32 C16 33 6 26 4 17 Z"
      stroke="#C2A04A"
      strokeWidth="0.8"
      opacity="0.5"
    />
    {/* Central midrib */}
    <path
      d="M4 17 L66 16"
      stroke="#C2A04A"
      strokeWidth="1.3"
      strokeLinecap="round"
      opacity="0.75"
    />
    {/* Lateral veins — upper half */}
    <path d="M14 16 L12 9" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.45" />
    <path d="M22 16 L20 7" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.45" />
    <path d="M30 16 L28 5" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.45" />
    <path d="M38 16 L37 5" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.45" />
    <path d="M46 16 L46 7" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.4" />
    <path d="M54 16 L55 10" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.35" />
    {/* Lateral veins — lower half */}
    <path d="M14 16 L12 24" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.45" />
    <path d="M22 16 L20 26" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.45" />
    <path d="M30 16 L28 28" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.45" />
    <path d="M38 16 L37 28" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.45" />
    <path d="M46 16 L46 26" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.4" />
    <path d="M54 16 L55 23" stroke="#C2A04A" strokeWidth="0.55" strokeLinecap="round" opacity="0.35" />
    {/* Short stem on left */}
    <path
      d="M4 17 C2 17 0 18 0 18"
      stroke="#C2A04A"
      strokeWidth="1.2"
      strokeLinecap="round"
      opacity="0.6"
    />
  </svg>
);


// Classical Filigree Ornamental Divider (matching screenshot)
const OrnamentalDivider = () => (
  <svg
    viewBox="0 0 460 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full max-w-[320px] sm:max-w-[420px] md:max-w-[480px] h-auto opacity-95 select-none pointer-events-none"
  >
    {/* Left horizontal line */}
    <line x1="15" y1="14" x2="175" y2="14" stroke="#C2A04A" strokeWidth="1.2" strokeLinecap="round" opacity="0.85" />
    <circle cx="180" cy="14" r="2" fill="#C2A04A" />

    {/* Center filigree crest */}
    <g transform="translate(230, 14)">
      {/* Central top dot */}
      <circle cx="0" cy="0" r="2.8" fill="#C2A04A" />

      {/* Flanking top pearls */}
      <circle cx="-16" cy="0" r="1.8" fill="#C2A04A" />
      <circle cx="16" cy="0" r="1.8" fill="#C2A04A" />

      {/* Central downward floral teardrop */}
      <path
        d="M0 3 C3 9 5 15 0 22 C-5 15 -3 9 0 3 Z"
        fill="#C2A04A"
      />
      <path
        d="M0 6 C1.8 10 2.8 14 0 18 C-2.8 14 -1.8 10 0 6 Z"
        fill="#120A06"
      />

      {/* Left curling scroll flourish */}
      <path
        d="M-2 1 C-8 -4 -20 -3 -22 4 C-24 11 -14 14 -9 9 C-5 5 -4 1 0 0"
        stroke="#C2A04A"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M-13 4 C-17 7 -18 11 -14 13 C-10 15 -6 11 -8 7"
        stroke="#C2A04A"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />

      {/* Right curling scroll flourish */}
      <path
        d="M2 1 C8 -4 20 -3 22 4 C24 11 14 14 9 9 C5 5 4 1 0 0"
        stroke="#C2A04A"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M13 4 C17 7 18 11 14 13 C10 15 6 11 8 7"
        stroke="#C2A04A"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />
    </g>

    {/* Right horizontal line */}
    <circle cx="280" cy="14" r="2" fill="#C2A04A" />
    <line x1="285" y1="14" x2="445" y2="14" stroke="#C2A04A" strokeWidth="1.2" strokeLinecap="round" opacity="0.85" />
  </svg>
);

const Hero = ({ start }) => {
  const { openReservationModal } = useReservation();
  const handleViewMenu = () => {
    const el = document.getElementById("table");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.scrollBy({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <section
      id="entrance"
      data-chapter
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden"
    >
      {/* ── Background Video ── */}
      <MotionBackgroundVideo
        className="absolute inset-0 h-full w-full object-cover origin-center"
        src={VIDEO.entrance}
        autoPlay={start}
        preload="auto"
        initial={{ scale: 1.25, opacity: 0 }}
        animate={start ? { scale: 1.15, opacity: 1 } : { scale: 1.2, opacity: 0 }}
        transition={{ duration: 2.2, ease: EASE }}
      />

      {/* ── Cinematic Dark Overlays (Vignette) ── */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#160F0B]/90 via-[#160F0B]/26 to-[#160F0B]/58 z-10 pointer-events-none" />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 75% 70% at 50% 50%, transparent 0%, rgba(22,15,11,0.5) 65%, rgba(22,15,11,0.85) 100%)",
        }}
      />

      {/* ── Main Hero Content ── */}
      <div className="relative z-20 flex w-full max-w-[1180px] flex-col items-center px-4 py-24 text-center sm:px-7 md:px-8 md:py-32">

        {/* ── Centered Stadium Logo Plaque ── */}
        <motion.div
          className="z-30 mb-5 pointer-events-auto md:mb-7"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={start ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.2, ease: EASE }}
        >
          <div
            className="relative flex flex-col items-center justify-center gap-1.5 sm:gap-2.5 rounded-full px-3 py-4 sm:px-4 sm:py-5 shadow-[0_12px_40px_rgba(0,0,0,0.6)] transition-transform duration-300 hover:scale-105 w-[180px] h-[230px] sm:w-[215px] sm:h-[275px] md:w-[240px] md:h-[310px] overflow-hidden"
            style={{
              background: "linear-gradient(175deg, #E4C18B 0%, #CA9F64 50%, #B38648 100%)",
              border: "4px solid #3E2412",
              boxShadow: "0 0 0 2.5px #DCB680, 0 0 0 5.5px #3E2412, 0 16px 45px rgba(0,0,0,0.65)",
            }}
          >
            {/* Inner Decorative Plaque Outline */}
            <div className="absolute inset-[6px] sm:inset-[7px] md:inset-[8px] rounded-full border-2 border-[#4A2D17]/60 pointer-events-none" />

            {/* Chef Jomon + #VEEDU + Stars Logo Image */}
            <img
              src="/Images/logo_new_black.png"
              alt="Chef Jomon's #VEEDU"
              className="w-[82%] sm:w-[84%] h-auto max-h-[125px] sm:max-h-[155px] md:max-h-[175px] object-contain z-10 filter contrast-150"
            />

            {/* Bold Thick Black Tagline directly below stars - strictly confined inside oval shape */}
            <div className="z-10 text-[7px] sm:text-[8.5px] md:text-[9.5px] font-sans tracking-[0.03em] text-[#080402] font-black uppercase leading-tight text-center px-1 max-w-[88%]">
              <div className="whitespace-nowrap">VIBRANT &nbsp;|&nbsp; EARTHY &nbsp;|&nbsp; ENTICING</div>
              <div className="whitespace-nowrap mt-0.5 sm:mt-1">DELECTABLE &nbsp;|&nbsp; UMAMI</div>
            </div>
          </div>
        </motion.div>

        {/* ── Main Headline: A MODERN KERALA CUISINE WITH PAN-INDIAN INFLUENCES ── */}
        <motion.h1
          className="font-sans text-center font-black tracking-[0.06em] sm:tracking-[0.08em] md:tracking-[0.1em] uppercase leading-[1.14] sm:leading-[1.18]"
          initial={{ opacity: 0, y: 28 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
        >
          <span className="block text-[clamp(1.45rem,4.2vw,3.25rem)] bg-gradient-to-b from-[#FFF6E8] via-[#F4E1BD] to-[#D6AE68] bg-clip-text text-transparent drop-shadow-[0_2px_12px_rgba(214,174,104,0.18)]">
            A MODERN KERALA CUISINE
          </span>
          <span className="block text-[clamp(1.45rem,4.2vw,3.25rem)] mt-1 sm:mt-1.5 bg-gradient-to-b from-[#FFF6E8] via-[#F4E1BD] to-[#D6AE68] bg-clip-text text-transparent drop-shadow-[0_2px_12px_rgba(214,174,104,0.18)]">
            WITH PAN-INDIAN INFLUENCES
          </span>
        </motion.h1>

        {/* ── Classical Filigree Ornamental Divider ── */}
        <motion.div
          className="my-4 sm:my-5 md:my-6 flex items-center justify-center w-full"
          initial={{ opacity: 0, scaleX: 0.6 }}
          animate={start ? { opacity: 1, scaleX: 1 } : {}}
          transition={{ duration: 1, delay: 0.75, ease: EASE }}
        >
          <OrnamentalDivider />
        </motion.div>

        {/* ── Sub-headline: An Extraordinary Dining Experience Awaits. (Single Line) ── */}
        <motion.div
          className="font-sans text-center font-bold tracking-[0.06em] sm:tracking-[0.08em] md:tracking-[0.1em] leading-normal"
          initial={{ opacity: 0, y: 20 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
        >
          <span className="inline-block whitespace-nowrap text-[clamp(0.76rem,2.7vw,1.45rem)] tracking-[0.06em] sm:tracking-[0.08em] md:tracking-[0.1em] [word-spacing:0.18em] bg-gradient-to-b from-[#F9E8C7] via-[#E2BD75] to-[#BA8E3A] bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(226,189,117,0.22)]">
            An Extraordinary Dining Experience Awaits.
          </span>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col items-center justify-center gap-3.5 sm:flex-row sm:flex-wrap sm:gap-4 mt-6 sm:mt-8 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 1.05, ease: EASE }}
        >
          {/* Reserve Table Button */}
          <button
            type="button"
            onClick={() => openReservationModal()}
            data-testid="hero-reserve-btn"
            className="flex h-[52px] w-[220px] sm:w-[205px] md:w-[215px] items-center justify-center gap-2.5 rounded-full font-sans text-[11px] font-medium tracking-[0.18em] uppercase shadow-[0_10px_30px_rgba(0,0,0,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg sm:h-[56px] sm:text-[11.5px] cursor-pointer"
            style={{
              backgroundColor: "rgba(28, 18, 12, 0.85)",
              border: "1px solid #C2A04A",
              color: "#F4EBDD",
              backdropFilter: "blur(6px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#C2A04A";
              e.currentTarget.style.color = "#160F0B";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(28, 18, 12, 0.85)";
              e.currentTarget.style.color = "#F4EBDD";
            }}
          >
            {/* Utensils Icon */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
              <path d="M7 2v20" />
              <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
            </svg>
            RESERVE TABLE
          </button>

          {/* View Menu Button */}
          <button
            onClick={handleViewMenu}
            data-testid="hero-menu-btn"
            className="flex h-[52px] w-[220px] sm:w-[205px] md:w-[215px] items-center justify-center gap-2.5 rounded-full font-sans text-[11px] font-medium tracking-[0.18em] uppercase shadow-[0_10px_30px_rgba(0,0,0,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg sm:h-[56px] sm:text-[11.5px]"
            style={{
              backgroundColor: "#F4EBDD",
              color: "#160F0B",
              border: "1px solid #F4EBDD",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#E8D19A";
              e.currentTarget.style.borderColor = "#E8D19A";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#F4EBDD";
              e.currentTarget.style.borderColor = "#F4EBDD";
            }}
          >
            {/* Menu Book Icon */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            VIEW MENU
          </button>

          {/* Navigate to Car Parking Button */}
          <a
            href={CONTACT.parkingMaps || "https://www.google.com/maps/dir/?api=1&destination=Fentiman+Way+Car+Park+Hornchurch+RM11+1QX"}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-parking-btn"
            title="Nearest parking: Fentiman Way Car Park, RM11 1QX (2 Min Walking)"
            className="flex h-[52px] w-[220px] sm:w-[205px] md:w-[215px] items-center justify-center gap-2.5 rounded-full font-sans text-[11px] font-medium tracking-[0.18em] uppercase shadow-[0_10px_30px_rgba(0,0,0,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg sm:h-[56px] sm:text-[11.5px]"
            style={{
              backgroundColor: "rgba(28, 18, 12, 0.85)",
              border: "1px solid #C2A04A",
              color: "#F4EBDD",
              backdropFilter: "blur(6px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#C2A04A";
              e.currentTarget.style.color = "#160F0B";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(28, 18, 12, 0.85)";
              e.currentTarget.style.color = "#F4EBDD";
            }}
          >
            {/* Navigation / Compass Arrow Icon */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="3 11 22 2 13 21 11 13 3 11" />
            </svg>
            NEAREST CAR PARKING
          </a>
        </motion.div>
      </div>

      {/* ── Scroll Cue ── */}
      <motion.div
        data-testid="scroll-cue"
        className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2.5 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ delay: 1.8, duration: 1 }}
      >
        <span className="font-body text-[9px] tracking-[0.45em] uppercase text-[#F4EBDD]/50 font-light">
          SCROLL TO ENTER
        </span>
        <span className="block h-10 w-px overflow-hidden">
          <span className="block h-full w-full animate-cue bg-[#C2A04A]/70" />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
